import React, { useContext, useCallback, useEffect } from 'react';
import { flowExecutor } from '../api/be-connector.js';
import { isComponent } from '../api/componentChecker.js';
import { filterByPage, getFlowParams } from '../api/eventParser.js';
import { getAttrValue } from '../api/propChecker.js';
import { useAppSelector, useAppDispatch, usePrevious } from '../hooks/hooks.js';
import { store } from '../store/index.js';
import { selectAddedExecutedOnLoad, addExecutedOnLoad } from '../store/reducers/executionSlice.js';
import { selectSubscribedVariables } from '../store/reducers/flowSlice.js';
import { setCurrentPage, createVariablesByNameAndCategorySelector, selectAppAndCurrentPageVariables } from '../store/reducers/variablesSlice.js';
import { isComponentChild } from '../util.js';
import GenericWrapper from './GenericWrapper.js';
import { PageIdContext } from './pageIdContext.js';
import { useNavigate } from 'react-router-dom';
import { PagePathsContext } from './pagePathsContext.js';
import { Helmet } from 'react-helmet';
import { isEqual } from '../../util/isEqual.js';

const executeUseEffect = (events, variables) => {
    for (let key in events) {
        const flowIds = events[key].map((eventObj) => eventObj.exec);
        const flowParams = events[key].map((eventObj) => getFlowParams(eventObj.params, variables));
        if (store.getState().engineProperties.mode === "live")
            flowIds.forEach((flowId, index) => {
                store.dispatch(addExecutedOnLoad(flowId));
                const input = Object.assign(Object.assign({}, flowParams[index]), variables);
                flowExecutor.trigger(flowId, input);
            });
    }
};
const childrenCreator = (children, components, item) => {
    let newChildren = [];
    for (let component of children) {
        if (isComponent(component)) {
            newChildren.push(componentCreator(component, Object.assign(Object.assign({}, components), { item })));
        }
        else {
            newChildren.push(getAttrValue(component, {
                item,
                app: store.getState().variables.app,
                page: store.getState().variables.page
            }, store.getState().components));
        }
    }
    return newChildren;
};
const componentCreator = (component, components, variables) => {
    let newChildren = [];
    if (component.children.length > 0) {
        newChildren = childrenCreator(component.children.map((child) => {
            if (isComponentChild(child)) {
                return components[child.value];
            }
            return child.value;
        }), components, variables && variables.item ? variables.item : {});
    }
    if (component.ignoreError) {
        return (React.createElement(GenericWrapper, { comp: component, ignoreError: true }, newChildren));
    }
    else {
        return (React.createElement(GenericWrapper, { comp: component, item: variables && variables.item }, newChildren));
    }
};
const PageRoot = ({ events, children }) => {
    const variables = useAppSelector(selectAppAndCurrentPageVariables);
    useEffect(() => {
        executeUseEffect(events, variables);
    }, [events]);
    // write useEffect for watchlist here
    // createWatchListVariableSelector should be written
    // call executeFlow with watchlist variables
    return React.createElement(React.Fragment, null, children);
};
const ComponentManager = ({ pageId, pageName, rootComponentId, components, events, appName }) => {
    const watchListFlowMap = useAppSelector(selectSubscribedVariables);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const pagePaths = useContext(PagePathsContext);
    const navigateToPageById = useCallback((id) => {
        if (pagePaths === null)
            return;
        navigate(pagePaths[id]);
    }, [navigate, pagePaths]);
    const variableDescriptors = () => {
        const descriptors = [];
        watchListFlowMap.forEach((watchFlowMap) => {
            descriptors.push(watchFlowMap.variable);
        });
        return descriptors;
    };
    useEffect(() => {
        flowExecutor.setOnNavigateCallback(navigateToPageById);
    }, [navigate]);
    useEffect(() => {
        dispatch(setCurrentPage(pageId));
    }, [dispatch, pageId]);
    const executedOnload = useAppSelector(selectAddedExecutedOnLoad);
    const watchlistVariables = useAppSelector(createVariablesByNameAndCategorySelector(pageId, variableDescriptors()));
    const getDeps = () => {
        const deps = [];
        const variableDescriptorList = variableDescriptors();
        variableDescriptorList.forEach((variableDescriptorItem) => {
            deps.push(watchlistVariables[variableDescriptorItem.scope][variableDescriptorItem.name]);
        });
        return deps;
    };
    const prev = usePrevious(watchlistVariables);
    const recursiveCheck = (obj, prev, diff) => {
        if (typeof obj === "object") {
            for (let key in obj) {
                if (typeof obj[key] === "object" &&
                    obj.hasOwnProperty("app") &&
                    obj.hasOwnProperty("page")) {
                    recursiveCheck(obj[key], prev[key], diff);
                }
                else {
                    if (!isEqual(obj[key], prev[key])) {
                        diff.push(key);
                    }
                }
            }
        }
    };
    useEffect(() => {
        const diff = [];
        if (prev)
            recursiveCheck(watchlistVariables, prev, diff);
        if (diff.length > 0) {
            watchListFlowMap.forEach((flowMap) => {
                if (diff.includes(flowMap.variable.name) &&
                    executedOnload.includes(flowMap.flowId)) {
                    const variables = filterByPage(store.getState().variables, pageId);
                    flowExecutor.trigger(flowMap.flowId, variables);
                }
            });
        }
    }, [...getDeps(), prev]);
    return components ? (React.createElement(PageIdContext.Provider, { value: pageId },
        React.createElement(Helmet, null,
            React.createElement("title", null,
                " ", appName !== null && appName !== void 0 ? appName : "",
                " | ",
                pageName)),
        React.createElement(PageRoot, { events: events }, componentCreator(components[rootComponentId], components)))) : (React.createElement("div", null, "Loading..."));
};

export { ComponentManager, componentCreator };
//# sourceMappingURL=ComponentManager.js.map
